import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/auth-store";
import { liveopsOperation, liveopsRead } from "@/lib/classic-liveops-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ClassicWorkspace,
  DataTable,
  Pagination,
  QueryState,
  classicDate,
} from "./ClassicWorkspace";
import { ClassicRewardEditor, type Reward } from "./ClassicRewards";

interface Coupon {
  id: string;
  revision: number;
  code: string;
  title: string;
  rewards: Reward[];
  enabled: boolean;
  startsAt: number | null;
  expiresAt: number | null;
  maxClaims: number | null;
  playerIds: string[] | null;
  claimCount?: number;
  createdAt?: number;
}
interface Draft {
  code: string;
  title: string;
  rewards: Reward[];
  enabled: boolean;
  startsAt: string;
  expiresAt: string;
  maxClaims: string;
  targets: string;
}
const emptyDraft = (): Draft => ({
  code: "",
  title: "",
  rewards: [],
  enabled: true,
  startsAt: "",
  expiresAt: "",
  maxClaims: "",
  targets: "",
});
function localDate(value: number | null) {
  if (value === null) return "";
  const date = new Date(value);
  return new Date(value - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}
function draftFrom(coupon: Coupon): Draft {
  return {
    code: coupon.code,
    title: coupon.title,
    rewards: coupon.rewards,
    enabled: coupon.enabled,
    startsAt: localDate(coupon.startsAt),
    expiresAt: localDate(coupon.expiresAt),
    maxClaims: coupon.maxClaims === null ? "" : String(coupon.maxClaims),
    targets: coupon.playerIds?.join("\n") ?? "",
  };
}
function payload(draft: Draft): Record<string, unknown> {
  const startsAt = draft.startsAt ? new Date(draft.startsAt).getTime() : null;
  const expiresAt = draft.expiresAt
    ? new Date(draft.expiresAt).getTime()
    : null;
  const maxClaims = draft.maxClaims.trim() ? Number(draft.maxClaims) : null;
  const playerIds = [...new Set(draft.targets.split(/[\s,]+/).filter(Boolean))];
  if (!/^[A-Z0-9_-]{3,64}$/.test(draft.code.trim().toUpperCase()))
    throw new Error(
      "Use a code with 3–64 letters, numbers, underscores or hyphens.",
    );
  if (!draft.title.trim() || draft.title.length > 200)
    throw new Error("Enter a title of 1–200 characters.");
  if (!draft.rewards.length)
    throw new Error("Add at least one Classic reward.");
  if (
    (startsAt !== null && !Number.isSafeInteger(startsAt)) ||
    (expiresAt !== null && !Number.isSafeInteger(expiresAt))
  )
    throw new Error("Enter valid schedule dates.");
  if (startsAt !== null && expiresAt !== null && expiresAt <= startsAt)
    throw new Error("Expiry must be after the start date.");
  if (maxClaims !== null && (!Number.isSafeInteger(maxClaims) || maxClaims < 1))
    throw new Error("The global claim cap must be a positive whole number.");
  if (playerIds.length > 1000 || playerIds.some((id) => id.length > 128))
    throw new Error(
      "Enter at most 1,000 player IDs, each up to 128 characters.",
    );
  return {
    code: draft.code.trim().toUpperCase(),
    title: draft.title.trim(),
    rewards: draft.rewards,
    enabled: draft.enabled,
    startsAt,
    expiresAt,
    maxClaims,
    playerIds: playerIds.length ? playerIds : null,
  };
}

export function ClassicCouponsRoute() {
  const me = useAuthStore((state) => state.me);
  return (
    <ClassicCoupons
      key={`${me?.discordId}:${me?.role}`}
      scope={me?.discordId ?? ""}
      role={me?.role ?? "guest"}
    />
  );
}
function ClassicCoupons({
  scope,
  role,
}: {
  scope: string;
  role: "internal" | "manager" | "guest";
}) {
  const queryClient = useQueryClient();
  const writable = !!scope && (role === "internal" || role === "manager");
  const alive = useRef(true);
  const submitLock = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const current = () =>
    alive.current &&
    useAuthStore.getState().me?.discordId === scope &&
    useAuthStore.getState().me?.role === role;
  const [before, setBefore] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [operation, setOperation] = useState<ReturnType<
    typeof liveopsOperation
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const queryKey = ["classic", "liveops", scope, role, "coupons"];
  const list = useQuery({
    queryKey: [...queryKey, "list", before],
    queryFn: ({ signal }) =>
      liveopsRead<{ coupons: Coupon[]; nextBefore: string | null }>(
        "coupons",
        "list",
        { limit: 30, ...(before ? { before } : {}) },
        signal,
      ),
    enabled: !!scope,
    retry: false,
  });
  const detail = useQuery({
    queryKey: [...queryKey, "detail", selected],
    queryFn: ({ signal }) =>
      liveopsRead<{ coupon: Coupon }>(
        "coupons",
        "detail",
        { id: selected },
        signal,
      ),
    enabled: !!scope && !!selected,
    retry: false,
  });
  useEffect(() => {
    if (detail.data && !operation && !busy)
      setDraft(draftFrom(detail.data.coupon));
  }, [detail.data]);
  const locked = busy || operation !== null;
  const editing = creating || selected !== null;
  const refresh = () => queryClient.invalidateQueries({ queryKey });
  function newCoupon() {
    if (locked) return;
    setSelected(null);
    setCreating(true);
    setDraft(emptyDraft());
    setError("");
    setNotice("");
  }
  function select(coupon: Coupon) {
    if (locked) return;
    setSelected(coupon.id);
    setCreating(false);
    setDraft(draftFrom(coupon));
    setError("");
    setNotice("");
  }
  async function run(action: "create" | "update" | "disable") {
    if (!writable || submitLock.current || !current()) return;
    let pending = operation;
    if (!pending) {
      try {
        if (action !== "create" && !detail.data) return;
        const args =
          action === "disable"
            ? { id: selected, expectedRevision: detail.data!.coupon.revision }
            : {
                ...payload(draft),
                ...(action === "update"
                  ? {
                      id: selected,
                      expectedRevision: detail.data!.coupon.revision,
                    }
                  : {}),
              };
        pending = liveopsOperation("coupons", action, args);
        setOperation(pending);
      } catch (caught) {
        setError((caught as Error).message);
        return;
      }
    }
    submitLock.current = true;
    setBusy(true);
    setError("");
    try {
      const result = (await pending.run()) as { coupon?: Coupon; id?: string };
      if (!current()) return;
      setOperation(null);
      setCreating(false);
      setSelected(result.coupon?.id ?? result.id ?? selected);
      setNotice(
        "Classic coupon saved. Existing grant receipts retain their original rewards.",
      );
      await refresh();
    } catch (caught) {
      if (current()) setError((caught as Error).message);
    } finally {
      submitLock.current = false;
      if (current()) setBusy(false);
    }
  }
  async function discard() {
    if (busy) return;
    setBusy(true);
    setOperation(null);
    setError("");
    setNotice(
      "Request discarded locally. Refreshing the current server record; an earlier request may already have succeeded.",
    );
    try {
      if (selected) {
        const fresh = await detail.refetch();
        if (current() && fresh.data) setDraft(draftFrom(fresh.data.coupon));
      }
      if (current()) await refresh();
    } finally {
      if (current()) setBusy(false);
    }
  }
  const patch = (value: Partial<Draft>) =>
    setDraft((previous) => ({ ...previous, ...value }));
  return (
    <ClassicWorkspace title="Coupons" notice={false}>
      <p className="text-sm text-muted-foreground">
        Online Classic codes use account eligibility and durable reward
        receipts.{" "}
        {writable
          ? "Create and manage codes here."
          : "Your access is read-only."}
      </p>
      {notice && (
        <p role="status" className="rounded border p-3 text-sm">
          {notice}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {writable && (
          <Button disabled={locked} onClick={newCoupon}>
            New coupon
          </Button>
        )}
        <Button
          variant="outline"
          disabled={locked}
          onClick={() => void refresh()}
        >
          Refresh
        </Button>
      </div>
      <QueryState query={list} />
      {list.data && (
        <>
          <DataTable
            headings={[
              "Code",
              "Title",
              "Status",
              "Schedule",
              "Claims",
              "Audience",
              "Actions",
            ]}
            rows={list.data.coupons.map((coupon) => [
              <code>{coupon.code}</code>,
              coupon.title,
              coupon.enabled ? "Enabled" : "Disabled",
              <span>
                From {classicDate(coupon.startsAt)}
                <br />
                Until {classicDate(coupon.expiresAt)}
              </span>,
              `${coupon.claimCount ?? 0} / ${coupon.maxClaims ?? "Unlimited"}`,
              coupon.playerIds
                ? `${coupon.playerIds.length.toLocaleString()} accounts`
                : "All accounts",
              <Button
                variant="outline"
                disabled={locked}
                onClick={() => select(coupon)}
              >
                View
              </Button>,
            ])}
          />
          <Pagination
            next={locked ? null : list.data.nextBefore}
            paged={!locked && !!before}
            onNext={() => setBefore(list.data!.nextBefore!)}
            onReset={() => setBefore("")}
          />
        </>
      )}
      {editing && (
        <section
          className="space-y-4 rounded-lg border p-5"
          aria-label="Coupon editor"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold">
              {creating ? "New coupon" : `Coupon ${draft.code}`}
            </h2>
            <Button
              variant="outline"
              disabled={locked}
              onClick={() => {
                setSelected(null);
                setCreating(false);
                setError("");
              }}
            >
              Close
            </Button>
          </div>
          {selected && <QueryState query={detail} />}
          {(creating || detail.data) && (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void run(creating ? "create" : "update");
              }}
            >
              <fieldset disabled={!writable || locked} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-1 text-sm">
                    Code
                    <Input
                      aria-label="Coupon code"
                      maxLength={64}
                      value={draft.code}
                      disabled={!creating}
                      onChange={(event) =>
                        patch({ code: event.target.value.toUpperCase() })
                      }
                    />
                    <span className="text-xs text-muted-foreground">
                      The code stays fixed after creation.
                    </span>
                  </label>
                  <label className="space-y-1 text-sm">
                    Title
                    <Input
                      aria-label="Coupon title"
                      maxLength={200}
                      value={draft.title}
                      onChange={(event) => patch({ title: event.target.value })}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    Starts at (your local time)
                    <Input
                      aria-label="Coupon start"
                      type="datetime-local"
                      value={draft.startsAt}
                      onChange={(event) =>
                        patch({ startsAt: event.target.value })
                      }
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    Expires at (your local time)
                    <Input
                      aria-label="Coupon expiry"
                      type="datetime-local"
                      value={draft.expiresAt}
                      onChange={(event) =>
                        patch({ expiresAt: event.target.value })
                      }
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    Global claim cap
                    <Input
                      aria-label="Coupon global claim cap"
                      type="number"
                      min={1}
                      step={1}
                      placeholder="Unlimited"
                      value={draft.maxClaims}
                      onChange={(event) =>
                        patch({ maxClaims: event.target.value })
                      }
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      aria-label="Coupon enabled"
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(event) =>
                        patch({ enabled: event.target.checked })
                      }
                    />
                    Enabled
                  </label>
                </div>
                <label className="block space-y-1 text-sm">
                  Target player IDs (optional)
                  <textarea
                    aria-label="Coupon target player IDs"
                    className="w-full rounded border bg-background p-2"
                    rows={3}
                    value={draft.targets}
                    onChange={(event) => patch({ targets: event.target.value })}
                  />
                  <span className="text-xs text-muted-foreground">
                    Leave empty for all accounts, or enter up to 1,000 IDs
                    separated by lines or commas.
                  </span>
                </label>
                <ClassicRewardEditor
                  value={draft.rewards}
                  onChange={(rewards) => patch({ rewards })}
                  disabled={!writable || locked}
                />
              </fieldset>
              {selected && detail.data && (
                <p className="text-xs text-muted-foreground">
                  Revision {detail.data.coupon.revision}. Each account can
                  redeem a code once. Reward edits apply to future claims.
                </p>
              )}
              {writable && !operation && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="submit"
                    disabled={busy || (!!selected && detail.isFetching)}
                  >
                    {creating ? "Create coupon" : "Save changes"}
                  </Button>
                  {selected && detail.data?.coupon.enabled && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy || detail.isFetching}
                      onClick={() => void run("disable")}
                    >
                      Disable coupon
                    </Button>
                  )}
                </div>
              )}
            </form>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {operation && (
            <div className="space-y-3 rounded border p-3 text-sm">
              <p>
                {busy
                  ? "Saving this request…"
                  : "The request remains fixed until resolved. Retry sends the same operation ID and payload. Discard refreshes server state before editing."}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={busy || !writable}
                  onClick={() => void run(creating ? "create" : "update")}
                >
                  {busy ? "Saving…" : "Retry same request"}
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => void discard()}
                >
                  Discard request and refresh
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </ClassicWorkspace>
  );
}
